import { Call, Device, TwilioError } from '@twilio/voice-sdk';
import { DialerClientType } from '../../../extViewmodels';
import { ApiClient } from '../../services/api';
import { isLiveCall } from '../../services/callActivity';
import { CallEndReporter } from '../../services/callEndReport';
import { claimTwilioDevice, releaseTwilioDevice } from '../../services/twilioDeviceLock';
import { CallEventBus } from './events';
import { announceLiveCall, watchOtherTabLiveCall } from './otherTabLiveCall';
import { CallEndCause, DialTarget, EndReason, IncomingCall } from './types';

type Options = {
	bus: CallEventBus;
	apiClient: ApiClient;
};

const STILL_OPEN_GRACE_MS = 5000;

/**
 * Twilio Device + Call wrapper. Owns all @twilio/voice-sdk interaction and emits
 * normalized pipeline events on the bus. Not a React hook — can be constructed
 * anywhere. Lifecycle: construct → init() → dial/accept/reject/hangup → deinit().
 *
 * Non-obvious Twilio behaviors preserved here (see call/README.md):
 *   - Device: allowIncomingWhileBusy: true + closeProtection message. A callback during a live
 *     call is held as the waiting call; the SDK plays no ring for it while the Device is busy
 *   - Token refresh on TokenWillExpire (not on a timer)
 *   - Error 31002 → auto-hangup, 31202 → re-init device
 *   - incomingCall.customParameters.get('autoAnswer') === 'True' → auto-accept
 *   - incomingCall.customParameters.get('phoneCallId') → server record lookup
 */
export class TwilioDeviceController {
	private readonly bus: CallEventBus;
	private readonly apiClient: ApiClient;
	private device: Device | undefined;
	private call: Call | undefined;
	private initPromise: Promise<boolean> | undefined;
	/** Stamps the cause before any teardown we initiate; see `services/callEndReport`. */
	private readonly reporter: CallEndReporter;
	/**
	 * Code from a `Call` `error` that has not settled yet. Twilio keeps retrying
	 * signaling for ~30s after one (`maxCallSignalingTimeoutMs`) and only `disconnect`
	 * ends a call, so the code waits here until the call recovers (cleared) or gives up
	 * (reported as the cause).
	 */
	private pendingCallErrorCode: number | undefined;
	private reconnectStartedAt: number | undefined;
	private blipError: TwilioError.TwilioError | undefined;
	private stillOpenCheck: ReturnType<typeof setTimeout> | undefined;
	private waitingCall: Call | undefined;
	private stopAnnouncingLiveCall: (() => void) | undefined;
	private stopWatchingOtherTabs: (() => void) | undefined;

	constructor({ bus, apiClient }: Options) {
		this.bus = bus;
		this.apiClient = apiClient;
		this.reporter = new CallEndReporter(apiClient);
		this.bus.on('call:ended', ({ reason, cause }) => {
			const call = this.call;
			if (!call || !isLiveCall(call)) return;
			clearTimeout(this.stillOpenCheck);
			this.stillOpenCheck = setTimeout(() => {
				if (this.call === call && isLiveCall(call)) {
					this.reporter.reportStillOpen(call, cause ?? 'pipeline-teardown', reason);
				}
			}, STILL_OPEN_GRACE_MS);
		});
	}

	/**
	 * Idempotent — concurrent callers (two Enable-audio clicks, `dial()`'s auto-init
	 * racing one) must never build a second Device on one identity: the orphan keeps
	 * emitting on the shared bus and steals `this.device` from under the live call.
	 */
	public async init(): Promise<boolean> {
		const state = this.device?.state;
		if (state === Device.State.Registered || state === Device.State.Registering) return true;
		if (this.device) {
			// Unregistered or destroyed. Re-registering the existing Device is only
			// safe with a fresh token — a `TokenWillExpire` refresh that didn't land
			// leaves the old one expired, and registering against it fails every
			// time. Rebuild instead, unless a call is still attached, which a rebuild
			// would disconnect.
			if (this.call) return this.reregister();
			this.deinit();
		}
		if (this.initPromise) return this.initPromise;
		this.initPromise = this.createDevice();
		try {
			return await this.initPromise;
		} finally {
			this.initPromise = undefined;
		}
	}

	private async reregister(): Promise<boolean> {
		const device = this.device;
		if (!device) return false;
		const token = await this.fetchToken();
		if (!token) {
			this.bus.emit('device:error', { message: 'Failed to get Twilio access token' });
			return false;
		}
		try {
			device.updateToken(token);
			await device.register();
			return true;
		} catch (err) {
			console.error('Failed to re-register Twilio device', err);
			return false;
		}
	}

	private async createDevice(): Promise<boolean> {
		this.bus.emit('device:initializing');

		// Claim the process-wide Twilio Device lock before constructing a
		// Device. If the legacy `TelephonyContextProvider` happens to have
		// one alive (e.g. mid-route-transition between `/queue` and
		// `/focused-view`), the registered evictor disposes it so we don't
		// run two Devices on the same identity. Re-claims by this same
		// controller (token re-init on 31202) are no-ops.
		claimTwilioDevice('focused-view-pipeline', () => this.deinit('device-evicted'));

		const token = await this.fetchToken();
		if (!token) {
			this.bus.emit('device:error', { message: 'Failed to get Twilio access token' });
			releaseTwilioDevice('focused-view-pipeline');
			return false;
		}

		window.addEventListener('pagehide', this.handlePageHide);
		const device = new Device(token, {
			allowIncomingWhileBusy: true,
			closeProtection: 'You are on a call. Closing this tab will end it.',
		});

		device.on(Device.EventName.Registered, () => this.bus.emit('device:registered'));
		device.on(Device.EventName.Unregistered, () => this.bus.emit('device:unregistered'));

		device.on(Device.EventName.TokenWillExpire, async () => {
			this.bus.emit('token:willExpire');
			const nextToken = await this.fetchToken();
			// Offline, eviction and the 31202 rebuild can tear this Device down while the fetch is in flight.
			if (!nextToken || this.device !== device || device.state === Device.State.Destroyed) return;
			try {
				device.updateToken(nextToken);
				this.bus.emit('token:refreshed');
			} catch (err) {
				console.error('Failed to refresh Twilio token', err);
			}
		});

		device.on(Device.EventName.Incoming, (incomingCall: Call) => {
			void this.handleIncoming(incomingCall);
		});

		device.on(Device.EventName.Error, (twilioError: TwilioError.TwilioError) => {
			if (twilioError.code === 31002) {
				// Call disconnected — hang up cleanly
				this.reporter.stampCause('device-error');
				this.hangup();
				return;
			}
			if (twilioError.code === 31202) {
				// Token has expired; re-initialize
				void this.init();
				return;
			}
			this.bus.emit('device:error', {
				code: twilioError.code,
				message: twilioError.message,
			});
		});

		device.on('offline', () => {
			this.deinit('device-offline');
			this.bus.emit('device:error', { message: 'Device went offline' });
		});

		this.stopWatchingOtherTabs?.();
		// The SDK starts the ring before emitting `Incoming`, so the mute has to be in place ahead of the invite.
		this.stopWatchingOtherTabs = watchOtherTabLiveCall(busy => device.audio?.incoming(!busy));

		device.register();
		this.device = device;
		return true;
	}

	public async dial(to: string, params?: Omit<DialTarget, 'to'>): Promise<boolean> {
		if (!this.device) {
			const ok = await this.init();
			if (!ok) return false;
		}

		const target: DialTarget = { to, ...params };
		this.bus.emit('call:dialing', target);

		try {
			// Pre-create the phone-call server record (matches legacy `initiateCall`
			// in `aida/contexts/telephony.tsx`). The Twilio gateway needs this
			// record to route the outbound connection; dialing without it returns
			// `ConnectionError 31005` from the gateway as soon as the call connects.
			let phoneCallId: string | undefined;
			if (params?.companyId) {
				const result = await this.apiClient.createPhoneCall({
					clientType: DialerClientType.Web,
					companyId: params.companyId,
					contactIds: params.contactId ? [params.contactId] : undefined,
					phoneNumber: to,
					preferredTelephonyConfigurationId: params.telephonyConfigurationId,
					aiConversationId: params.aiConversationId,
				});
				if (!result.success) {
					this.bus.emit('call:ended', { reason: 'error', cause: 'dial-failed' });
					console.error('createPhoneCall failed', result);
					return false;
				}
				phoneCallId = result.value?.id;
			}

			const connectParams: Record<string, string> = { To: to };
			if (params?.companyId) connectParams.companyId = params.companyId;
			if (params?.contactId) connectParams.contactId = params.contactId;
			if (params?.aiConversationId) connectParams.aiConversationId = params.aiConversationId;
			if (params?.telephonyConfigurationId) connectParams.telephonyConfigurationId = params.telephonyConfigurationId;
			if (phoneCallId) connectParams.phoneCallId = phoneCallId;

			const newCall = await this.device!.connect({ params: connectParams });
			this.attachCallListeners(newCall, 'outbound');
			return true;
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Dial failed';
			this.bus.emit('call:ended', { reason: 'error', cause: 'dial-failed' });
			console.error('Twilio dial failed:', message);
			return false;
		}
	}

	public accept(): void {
		if (this.call && this.call.status() === Call.State.Pending) {
			this.call.accept();
		}
	}

	public reject(): void {
		if (this.call && this.call.status() === Call.State.Pending) {
			this.call.ignore();
			this.bus.emit('call:rejected', { sid: this.callSid() });
			this.call = undefined;
		}
	}

	public ignoreWaiting(): void {
		const waiting = this.waitingCall;
		if (!waiting) return;
		this.waitingCall = undefined;
		waiting.ignore();
		this.bus.emit('call:waiting-ended', { sid: waiting.parameters.CallSid ?? '' });
	}

	public hangup(): void {
		if (this.device) {
			// Keep a more specific cause if one was already stamped — the Device error
			// path hangs up through here and must not be recorded as the rep doing it.
			this.reporter.stampCauseIfUnset('user-hangup');
			this.device.disconnectAll();
		}
	}

	public setMuted(muted: boolean): void {
		if (this.call) {
			this.call.mute(muted);
		}
	}

	/**
	 * Send DTMF tones on the active call. Only effective while the call is in
	 * the `Open` state (i.e. connected) — Twilio silently ignores digits
	 * sent at any other status. The argument is forwarded directly so callers
	 * can batch (`'1234'`) or dispatch one digit at a time. Mirrors the legacy
	 * `useTwilioDialer.sendDigits` behavior.
	 */
	public sendDigits(digits: string): void {
		if (!!this.call && this.call.status() === Call.State.Open) {
			this.call.sendDigits(digits);
		}
	}

	/**
	 * `cause` records who asked for the teardown, which is the whole diagnostic:
	 * from Twilio's side every path here looks like the client hanging up cleanly.
	 * Defaults to the React-cleanup path, its only unlabelled caller.
	 */
	public deinit(cause: CallEndCause = 'pipeline-teardown'): void {
		// Report before disconnecting: `destroy()` tears the listeners down, so the
		// `disconnect` event is not guaranteed to survive to carry the cause itself.
		if (this.call) {
			this.reporter.stampCause(cause);
			this.reportEnd(this.call, cause, 'hangup');
		}
		window.removeEventListener('pagehide', this.handlePageHide);
		clearTimeout(this.stillOpenCheck);
		this.ignoreWaiting();
		this.endLiveCall();
		this.stopWatchingOtherTabs?.();
		this.stopWatchingOtherTabs = undefined;
		if (this.device) {
			try {
				this.device.disconnectAll();
				this.device.destroy();
			} catch (err) {
				console.error('Failed to destroy Twilio device', err);
			} finally {
				this.device = undefined;
			}
		}
		// Release the process-wide Device lock so a subsequent legacy mount
		// (e.g. user navigates back to `/queue`) can claim its own Device
		// without an immediate eviction.
		releaseTwilioDevice('focused-view-pipeline');
	}

	private async fetchToken(): Promise<string | null> {
		const result = await this.apiClient.getTwilioAccessToken();
		return result.success ? result.value : null;
	}

	private async handleIncoming(incomingCall: Call): Promise<void> {
		const autoAnswer = incomingCall.customParameters.get('autoAnswer') === 'True';

		// Adopting a second leg mid-call would strand the live call's listeners, so it is held aside instead.
		if (isLiveCall(this.call)) {
			if (autoAnswer || this.waitingCall) {
				console.warn('ignoring an incoming call while already on one', incomingCall.parameters?.CallSid);
				incomingCall.ignore();
				return;
			}
			this.holdAsWaiting(incomingCall);
			return;
		}

		const phoneCallId = incomingCall.customParameters.get('phoneCallId') ?? undefined;
		const from = incomingCall.parameters.From ?? '';
		const sid = incomingCall.parameters.CallSid ?? '';

		this.attachCallListeners(incomingCall, 'inbound');

		if (autoAnswer) {
			incomingCall.accept();
			return;
		}

		const incoming: IncomingCall = { sid, phoneCallId, from, autoAnswer: false };
		this.bus.emit('call:incoming', incoming);
	}

	private holdAsWaiting(incomingCall: Call): void {
		const sid = incomingCall.parameters.CallSid ?? '';
		this.waitingCall = incomingCall;
		incomingCall.on('cancel', () => {
			if (this.waitingCall !== incomingCall) return;
			this.waitingCall = undefined;
			this.bus.emit('call:waiting-ended', { sid });
		});
		this.bus.emit('call:waiting', {
			sid,
			phoneCallId: incomingCall.customParameters.get('phoneCallId') ?? undefined,
			from: incomingCall.parameters.From ?? '',
		});
	}

	private endLiveCall(): void {
		this.stopAnnouncingLiveCall?.();
		this.stopAnnouncingLiveCall = undefined;
		const waiting = this.waitingCall;
		if (!waiting) return;
		this.waitingCall = undefined;
		this.bus.emit('call:waiting-ended', { sid: waiting.parameters.CallSid ?? '' });
		// Still ringing at the server, so it becomes the normal incoming call the rep can answer.
		if (waiting.status() === Call.State.Pending) void this.handleIncoming(waiting);
	}

	private attachCallListeners(newCall: Call, direction: 'inbound' | 'outbound'): void {
		this.call = newCall;
		this.reporter.trackCall(newCall, direction);
		// A superseded call's unsettled error must not be blamed for this one.
		this.pendingCallErrorCode = undefined;
		this.reconnectStartedAt = undefined;
		this.blipError = undefined;

		// Per-call listeners are never detached and a superseded Call still emits — an
		// ignored inbound leg fires `cancel` whenever the caller gives up, long after
		// the rep moved on — so drop events from any call that is no longer current.
		const isCurrent = () => this.call === newCall;

		newCall.on('accept', () => {
			if (!isCurrent()) return;
			this.reporter.markLive(newCall);
			this.stopAnnouncingLiveCall ??= announceLiveCall();
			const sid = newCall.parameters.CallSid ?? '';
			const peer =
				direction === 'inbound' ? (newCall.parameters.From ?? '') : (newCall.customParameters.get('To') ?? '');
			const phoneCallId = newCall.customParameters.get('phoneCallId') ?? undefined;
			this.bus.emit('call:accepted', { sid });
			this.bus.emit('call:connected', {
				sid,
				phoneCallId,
				direction,
				peer,
				startedAt: Date.now(),
			});
		});

		newCall.on('reject', () => {
			if (!isCurrent()) return;
			this.bus.emit('call:rejected', { sid: newCall.parameters.CallSid ?? '' });
			this.call = undefined;
			this.endLiveCall();
		});

		newCall.on('disconnect', () => {
			if (!isCurrent()) return;
			const errorCode = this.pendingCallErrorCode;
			this.pendingCallErrorCode = undefined;
			// No stamped cause means nothing here asked for it — the far end hung up, unless
			// an error was still outstanding, in which case the reconnect is what ran out.
			const cause = this.reporter.takeCause(errorCode === undefined ? 'remote-hangup' : 'call-error');
			this.reportEnd(newCall, cause, 'disconnect', errorCode);
			this.reconnectStartedAt = undefined;
			this.blipError = undefined;
			this.call = undefined;
			this.bus.emit('call:ended', { reason: 'disconnect', cause });
			this.endLiveCall();
		});

		newCall.on('cancel', () => {
			if (!isCurrent()) return;
			this.reportEnd(newCall, 'call-cancel', 'cancel');
			this.call = undefined;
			this.bus.emit('call:ended', { reason: 'cancel', cause: 'call-cancel' });
			this.endLiveCall();
		});

		newCall.on('error', (err: TwilioError.TwilioError) => {
			if (!isCurrent()) return;
			// Not an ending. Twilio confirmed the SDK retries signaling for ~30s after this
			// and that only `disconnect` settles a call, so ending here destroyed calls that
			// would have healed. Hold the code and show the rep a reconnect instead. The SDK
			// may or may not also emit `reconnecting`, so this is what guarantees the pill.
			this.pendingCallErrorCode = err.code;
			this.blipError = this.blipError ?? err;
			this.reporter.reportCallEvent(newCall, 'error', { errorCode: err.code, message: err.message });
			this.bus.emit('call:reconnecting', { message: err.message });
		});

		newCall.on('mute', (isMuted: boolean) => {
			if (!isCurrent()) return;
			this.bus.emit('call:mute-changed', { muted: isMuted });
		});

		newCall.on('reconnecting', (err: TwilioError.TwilioError) => {
			if (!isCurrent()) return;
			this.noteReconnecting(newCall, err);
			this.bus.emit('call:reconnecting', { message: err.message });
		});

		newCall.on('reconnected', () => {
			if (!isCurrent()) return;
			this.pendingCallErrorCode = undefined;
			if (this.reconnectStartedAt !== undefined || this.blipError) {
				// The earlier reports went out while the network was down, so this one repeats the cause.
				this.reporter.reportCallEvent(newCall, 'reconnected', {
					durationMs: this.reconnectStartedAt === undefined ? undefined : Date.now() - this.reconnectStartedAt,
					errorCode: this.blipError?.code,
					message: this.blipError?.message,
				});
			}
			this.reconnectStartedAt = undefined;
			this.blipError = undefined;
			this.bus.emit('call:reconnected');
		});
	}

	private noteReconnecting(call: Call, err: TwilioError.TwilioError): void {
		this.blipError = this.blipError ?? err;
		if (this.reconnectStartedAt !== undefined) return;
		this.reconnectStartedAt = Date.now();
		this.reporter.reportCallEvent(call, 'reconnecting', { errorCode: err.code, message: err.message });
	}

	private readonly handlePageHide = (): void => {
		if (!this.call || !isLiveCall(this.call)) return;
		this.reporter.report(this.call, 'page-unload', 'hangup', {
			deviceState: this.device?.state,
			deferToNextLoad: true,
		});
	};

	private callSid(): string {
		return this.call?.parameters.CallSid ?? '';
	}

	private reportEnd(call: Call, cause: CallEndCause, reason: EndReason, errorCode?: number): void {
		this.reporter.report(call, cause, reason, { deviceState: this.device?.state, errorCode });
	}
}
